import { apiFetch } from './projectsApi'
import { API_BASE_URL } from './apiClient'

export interface WorkspaceInstallation {
  id: string
  project_id: string
  hull_number: string | null
  vessel_name: string | null
  class_society: string | null
  function_name: string | null
  system_name: string | null
  system_description: string | null
  vendor_name_raw: string | null
  canonical_vendor: string | null
  recorded_vendor_id: string | null
  recorded_canonical_vendor: string | null
  maker_conflict: boolean
  maker_resolution_confidence: number | null
  maker_resolution_reasoning: string | null
  models: string[]
  has_ra: boolean
  has_inventory: boolean
  has_contacts: boolean
  evidence_count: number
}

export interface WorkspaceSystem {
  key: string
  canonical_system_id: string | null
  status: 'confirmed' | 'suggested' | 'inferred'
  auto_confirmed: boolean
  has_open_flag: boolean
  name: string
  type_name: string
  system_type_id: string | null
  canonical_vendor_id: string | null
  maker: string | null
  recorded_makers: string[]
  maker_conflict: boolean
  aliases: string[]
  function_code: string
  function_label: string
  function_order: number
  installation_count: number
  vessel_count: number
  vendors: string[]
  models: string[]
  linked_document_count: number
  coverage: {
    ra: number
    inventory: number
    contacts: number
    evidence: number
  }
  installations: WorkspaceInstallation[]
}

export interface WorkspaceFunction {
  code: string
  label: string
  order: number
  system_count: number
  installation_count: number
}

export interface WorkspaceTree {
  systems: WorkspaceSystem[]
  functions: WorkspaceFunction[]
  grain: {
    installations: number
    catalog_systems: number
    confirmed_systems: number
    suggested_systems: number
    inferred_systems: number
    vessels: number
    with_ra: number
    with_inventory: number
    with_contacts: number
    evidence_files: number
  }
}

export interface CatalogSuggestion {
  id: string
  proposed_name: string
  name_norm: string
  function_code: string
  canonical_vendor_id: string | null
  product_family: string | null
  functional_type_name: string | null
  member_ids: string[]
  confidence: number | null
  reasoning: string | null
  evidence: {
    maker?: string | null
    functional_type?: string
    aliases?: string[]
    vendors?: string[]
    models?: string[]
    hulls?: string[]
  }
  status: 'pending' | 'accepted' | 'rejected' | 'superseded'
  created_at: string
  model: string | null
  prompt_version: string | null
}

export interface CatalogAnalysisJob {
  id: string
  job_type: 'grouping' | 'maker_resolution'
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
  requested_by: string
  model: string | null
  prompt_version: string | null
  considered: number
  suggestions_created: number
  max_cost_usd: number
  cost_usd: number
  error: string | null
  heartbeat_at: string | null
  started_at: string | null
  finished_at: string | null
  created_at: string
}

export interface WorkspaceDocument {
  id: string
  sha256: string
  byte_size: number
  detected_mime: string | null
  extension: string | null
  integrity: string
  quarantine: string
  original_filename: string | null
  relative_path: string | null
  source_kind: string | null
  extraction_status: string | null
  method: string | null
  language: string | null
  page_count: number | null
  character_count: number | null
  preview_text: string | null
  analysis_status: string | null
  analysis: Record<string, unknown> | null
  link_count: number
}

export interface RelatedWorkspaceDocument {
  id: string
  sha256: string
  byte_size: number
  detected_mime: string | null
  extension: string | null
  integrity: string
  quarantine: string
  original_filename: string
  relative_path: string
  source_paths: string[]
  extraction_status: string | null
  method: string | null
  page_count: number | null
  character_count: number | null
  preview_text: string | null
  extraction_warnings: string[]
  analysis_status: string | null
  analysis: {
    title?: string
    document_type?: string
    summary?: string
    vendors?: string[]
    models?: string[]
    regulations?: string[]
    cyber_topics?: string[]
  } | null
  explicitly_linked: boolean
  explicit_links: Array<{
    id: string
    state: 'proposed' | 'confirmed'
    confidence: number
    reasoning: string | null
    citations: Array<{ page: number | null; quote: string }>
    prompt_version: string | null
    canonical_system_id: string | null
    project_system_id: string | null
  }>
  score: number
  match_reasons: string[]
}

export interface DocumentIngestJob {
  id: string
  mode: 'inventory' | 'copy' | 'extract' | 'analyze' | 'organize' | 'full' | 'update'
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
  source_root: string
  requested_by: string
  files_seen: number
  files_processed: number
  files_succeeded: number
  files_failed: number
  bytes_copied: number
  cost_usd?: number
  notes?: {
    phase?: string
    inventory_total?: number
    new_or_changed?: number
    unchanged?: number
    copied_objects?: number
    mapping_candidates?: number
    copy_processed?: number
    copy_succeeded?: number
    copy_failed?: number
    copy_bytes?: number
    extraction_processed?: number
    extraction_succeeded?: number
    extraction_failed?: number
    analysis_processed?: number
    analysis_succeeded?: number
    analysis_failed?: number
    cost_cap_reached?: boolean
  }
  error: string | null
  started_at: string | null
  finished_at: string | null
  heartbeat_at: string | null
  created_at: string
}

export interface DocumentIngestStatus {
  config: {
    source_root: string
    source_reachable_from_api: boolean
    storage_root: string
    storage_reachable_from_api: boolean
  }
  totals: {
    sources: number
    objects: number
    logical_bytes: number
    extracted: number
    analyzed: number
    linked: number
  }
  jobs: DocumentIngestJob[]
}

export interface DocumentMappingReviewItem {
  document_object_id: string
  original_filename: string
  relative_path: string
  extraction_status: string | null
  preview_text: string | null
  analysis: {
    title?: string
    document_type?: string
    summary?: string
    vendors?: string[]
    models?: string[]
    regulations?: string[]
  } | null
  link_id: string | null
  state: 'proposed' | null
  confidence: number | null
  reasoning: string | null
  citations: Array<{ page: number | null; quote: string }>
  canonical_system_id: string | null
  project_system_id: string | null
  target_label: string | null
  link_run_id: string
  link_result: Record<string, unknown> | null
  no_match_reason: string | null
}

export interface DocumentMappingReviewPage {
  items: DocumentMappingReviewItem[]
  page: number
  page_size: number
  total: number
}

export interface WorkspaceAssistantAnswer {
  answer: string
  citations: Array<{
    source_type: 'system' | 'document'
    source_id: string
    label: string
    route?: string | null
    page?: number | null
    fact: string
    quote: string
  }>
  limitations: string[]
  model?: string
  cost_usd: number
}

export const knowledgeWorkspaceApi = {
  tree: (query = '') =>
    apiFetch<WorkspaceTree>('GET', `/api/knowledge-workspace/tree${query ? `?q=${encodeURIComponent(query)}` : ''}`),
  askAssistant: (question: string) =>
    apiFetch<WorkspaceAssistantAnswer>('POST', '/api/knowledge-workspace/assistant', { question }),
  suggestions: (status: CatalogSuggestion['status'] | 'all' = 'pending') =>
    apiFetch<CatalogSuggestion[]>('GET', `/api/knowledge-workspace/suggestions?status=${status}`),
  bootstrapCatalog: () =>
    apiFetch<{
      considered: number
      created: number
      auto_confirmed: number
      auto_failed: Array<{ id: string; error: string }>
    }>('POST', '/api/knowledge-workspace/catalog/bootstrap'),
  catalogJobs: () =>
    apiFetch<CatalogAnalysisJob[]>('GET', '/api/knowledge-workspace/catalog/jobs'),
  analyzeCatalog: (maxCostUsd = 5) =>
    apiFetch<CatalogAnalysisJob>(
      'POST',
      '/api/knowledge-workspace/catalog/analyze',
      { confirmed: true, max_cost_usd: maxCostUsd },
    ),
  resolveProductMakers: (maxCostUsd = 3) =>
    apiFetch<CatalogAnalysisJob>(
      'POST',
      '/api/knowledge-workspace/catalog/resolve-makers',
      { confirmed: true, max_cost_usd: maxCostUsd },
    ),
  decideSuggestion: (
    id: string,
    action: 'accept' | 'reject',
    changes?: { name?: string; function_code?: string },
  ) => apiFetch<{ id: string; status: string; canonical_system_id?: string }>(
    'POST',
    `/api/knowledge-workspace/suggestions/${id}/decide`,
    { action, ...changes },
  ),
  flagCanonicalProduct: (id: string, reason?: string, projectSystemId?: string) =>
    apiFetch<{ flag_id: string; canonical_system_id: string; memberships_flagged: number }>(
      'POST',
      `/api/knowledge-workspace/catalog/systems/${id}/flag`,
      { reason, project_system_id: projectSystemId },
    ),
  flagMakerResolution: (projectSystemId: string, reason?: string) =>
    apiFetch<{ project_system_id: string; status: 'flagged' }>(
      'POST',
      `/api/knowledge-workspace/catalog/vendor-resolutions/${projectSystemId}/flag`,
      { reason },
    ),
  documents: (query = '') =>
    apiFetch<WorkspaceDocument[]>(
      'GET',
      `/api/knowledge-workspace/documents${query ? `?q=${encodeURIComponent(query)}` : ''}`,
    ),
  relatedDocuments: (system: WorkspaceSystem) =>
    apiFetch<RelatedWorkspaceDocument[]>(
      'POST',
      '/api/knowledge-workspace/documents/related',
      {
        canonical_system_id: system.canonical_system_id,
        name: system.name,
        type_name: system.type_name,
        maker: system.maker,
        aliases: system.aliases,
        models: system.models,
        installation_ids: system.installations.map(installation => installation.id),
      },
    ),
  extractRelatedDocuments: (objectIds: string[]) =>
    apiFetch<DocumentIngestJob>(
      'POST',
      '/api/knowledge-workspace/documents/extract-related',
      { object_ids: objectIds },
    ),
  analyzeRelatedDocuments: (objectIds: string[], maxCostUsd = 2) =>
    apiFetch<DocumentIngestJob>(
      'POST',
      '/api/knowledge-workspace/documents/analyze-related',
      { object_ids: objectIds, max_cost_usd: maxCostUsd, confirmed: true },
    ),
  organizeDocuments: (limit = 50, maxCostUsd = 5) =>
    apiFetch<DocumentIngestJob>(
      'POST',
      '/api/knowledge-workspace/documents/organize',
      { limit, max_cost_usd: maxCostUsd, confirmed: true },
    ),
  flagDocumentLink: (id: string, reason?: string) =>
    apiFetch<{ id: string; state: 'flagged' }>(
      'POST',
      `/api/knowledge-workspace/documents/links/${id}/flag`,
      { reason },
    ),
  mappingReview: (options?: { page?: number; pageSize?: number; status?: 'all' | 'proposed' | 'unmatched'; query?: string }) => {
    const params = new URLSearchParams({
      page: String(options?.page ?? 1),
      page_size: String(options?.pageSize ?? 25),
      status: options?.status ?? 'all',
    })
    if (options?.query) params.set('q', options.query)
    return apiFetch<DocumentMappingReviewPage>(
      'GET',
      `/api/knowledge-workspace/documents/mapping-review?${params}`,
    )
  },
  confirmDocumentLink: (id: string) =>
    apiFetch<{ id: string; document_object_id: string; state: 'confirmed' }>(
      'POST',
      `/api/knowledge-workspace/documents/links/${id}/confirm`,
    ),
  rejectDocumentLink: (id: string) =>
    apiFetch<{ id: string; document_object_id: string; state: 'rejected' }>(
      'POST',
      `/api/knowledge-workspace/documents/links/${id}/reject`,
    ),
  reassignDocument: (
    documentObjectId: string,
    targetType: 'canonical_system' | 'project_system',
    targetId: string,
  ) => apiFetch<{ id: string; document_object_id: string; state: 'confirmed' }>(
    'POST',
    `/api/knowledge-workspace/documents/${documentObjectId}/reassign`,
    { target_type: targetType, target_id: targetId },
  ),
  signDocument: async (id: string) => {
    const signed = await apiFetch<{ url: string; expires: number }>(
      'POST',
      `/api/knowledge-workspace/documents/${id}/sign`,
    )
    return { ...signed, url: `${API_BASE_URL}${signed.url}` }
  },
  ingestStatus: () =>
    apiFetch<DocumentIngestStatus>('GET', '/api/knowledge-workspace/ingest'),
  updateDump: (maxCostUsd = 100) =>
    apiFetch<DocumentIngestJob>(
      'POST',
      '/api/knowledge-workspace/ingest/update',
      { confirmed: true, max_cost_usd: maxCostUsd },
    ),
  queueIngest: (
    mode: 'inventory' | 'copy' | 'extract' | 'analyze',
    options?: { sample_limit?: number; max_cost_usd?: number; confirmed?: boolean },
  ) => apiFetch<DocumentIngestJob>(
    'POST',
    '/api/knowledge-workspace/ingest/jobs',
    { mode, ...options },
  ),
  cancelIngest: (id: string) =>
    apiFetch<Pick<DocumentIngestJob, 'id' | 'mode' | 'status'>>(
      'POST',
      `/api/knowledge-workspace/ingest/jobs/${id}/cancel`,
    ),
}
